import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  AssignmentTurnedIn as AssignIcon,
  VerifiedUser as QualityIcon,
  OpenInNew as OpenInNewIcon,
  Science as SixMIcon,
  HowToVote as ApproveIcon,
  ArrowForward as ArrowIcon,
  Person,
  Build,
  Category,
  Route,
  Straighten,
  Factory as BulkIcon,
  Replay as ResubmitIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import qualityManagementService from '../services/qualityManagement.service';
import SixMAnalysisDialog from '../components/SixMAnalysisDialog';
import EditExternalRejectionDialog from '../components/EditExternalRejectionDialog';
import EditNoIssueDialog from '../components/EditNoIssueDialog';
import BulkQualityManagement from './BulkQualityManagement';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TrelloCard {
  id: string;
  name: string;
  desc: string;
  labels: Array<{ id: string; name: string; color: string }>;
  due: string | null;
  url: string;
  listId: string;
  listName: string;
  boardId: string;
  boardName: string;
}

interface QualityReview {
  id: number;
  trello_card_id: string;
  trello_card_name: string;
  batch_number: string;
  source_list_name: string;
  source_board_name: string;
  main_rejection_type?: 'internal_ocl' | 'external_customer';
  sub_rejection_type?: 'printing_department' | 'other_department';
  rejection_type: 'internal' | 'external';
  machine_type: 'rotary' | 'digital';
  issue_type: 'color' | 'layout' | null;
  external_issues: string[] | null;
  external_other_issue: string | null;
  description: string | null;
  action_plan: string | null;
  assigned_to: string;
  status: 'pending' | 'in_review' | '6m_submitted' | 'aloka_approved' | 'ashan_approved' | 'isuru_approved' | 'resolved' | 'no_issue';
  no_issue: number | null;
  no_issue_remarks: string | null;
  sixm_category: 'man' | 'machine' | 'material' | 'method' | 'measurement' | null;
  sixm_data: Record<string, any> | null;
  sixm_entries: Array<{ category: 'man' | 'machine' | 'material' | 'method' | 'measurement'; links?: string[]; submitted_at?: string; submitted_by?: string; [key: string]: any }> | null;
  sixm_submitted_at: string | null;
  sixm_submitted_by: string | null;
  aloka_approved_at: string | null;
  aloka_approved_by: string | null;
  ashan_approved_at: string | null;
  ashan_approved_by: string | null;
  isuru_approved_at: string | null;
  isuru_approved_by: string | null;
  lasantha_approved_at: string | null;
  lasantha_approved_by: string | null;
  created_by_name: string;
  created_by_username: string;
  created_at: string;
  updated_at: string;
}

interface ResubmissionHistoryItem {
  id: number;
  original_review_id: number;
  new_review_id: number;
  resubmitted_by: number | null;
  resubmitted_by_username: string | null;
  resubmission_note: string | null;
  original_status: string | null;
  created_at: string;
  original_batch_number?: string | null;
  original_card_name?: string | null;
  original_current_status?: string | null;
  new_batch_number?: string | null;
  new_card_name?: string | null;
  new_current_status?: string | null;
  original_snapshot?: Record<string, any> | null;
}

interface Stats {
  total: number;
  pending: number;
  in_review: number;
  sixm_submitted: number;
  in_approval: number;
  resolved: number;
  internal: number;
  external: number;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const EXTERNAL_ISSUE_OPTIONS = ['Base Color', 'Absorption', 'Hand Feel', 'Other'];

const ASSIGNEE_COLORS: Record<string, string> = {
  KrishanOCL: '#1565C0',
  LahiruOCL:  '#2E7D32',
  AnjanaOCL:  '#6A1B9A',
  AlokaOCL:   '#BF360C',
};

const resolveAssignee = (
  mainRejectionType: 'internal_ocl' | 'external_customer',
  subRejectionType: 'printing_department' | 'other_department',
  machineType: 'rotary' | 'digital',
  issueType?: 'color' | 'layout' | ''
): string => {
  // Other department always goes to AlokaOCL
  if (subRejectionType === 'other_department') return 'AlokaOCL';
  
  // Printing department assignment depends on machine and issue type
  if (subRejectionType === 'printing_department') {
    if (machineType === 'rotary') {
      return issueType === 'color' ? 'LahiruOCL' : 'AnjanaOCL';
    } else { // digital
      return 'KrishanOCL'; // Both color and layout go to Krishan for digital
    }
  }
  
  // Fallback (shouldn't reach here)
  return 'AlokaOCL';
};

const APPROVAL_CHAIN = [
  { username: 'AlokaOCL',    label: 'Aloka',    tsField: 'aloka_approved_at'    as const, byField: 'aloka_approved_by'    as const },
  { username: 'AshanOCL',    label: 'Ashan',    tsField: 'ashan_approved_at'    as const, byField: 'ashan_approved_by'    as const },
  { username: 'IsuruOCL',    label: 'Isuru',    tsField: 'isuru_approved_at'    as const, byField: 'isuru_approved_by'    as const },
  { username: 'LasanthaOCL', label: 'Lasantha', tsField: 'lasantha_approved_at' as const, byField: 'lasantha_approved_by' as const },
];

const DELETE_ALLOWED_USERS = ['AlokaOCL', 'AshanOCL', 'IsuruOCL', 'LasanthaOCL'];

const STATUS_ORDER = ['pending','in_review','6m_submitted','aloka_approved','ashan_approved','isuru_approved','resolved'];

const statusColor = (s: string) => {
  if (s === 'resolved')      return 'success';
  if (s === 'in_review')     return 'warning';
  if (s === '6m_submitted')  return 'info';
  if (s.endsWith('_approved')) return 'secondary';
  return 'default';
};

const statusLabel = (s: string) =>
  s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const getDisplayCardNumber = (review: QualityReview): string => {
  const cardName = String(review.trello_card_name || '').trim();
  if (cardName) return cardName;
  return String(review.batch_number || '').trim();
};

// Sub-component: visual approval chain
const ApprovalChainDisplay: React.FC<{ review: QualityReview }> = ({ review }) => (
  <Box>
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Approval Chain</Typography>
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      {APPROVAL_CHAIN.map((step, i) => {
        const approved = !!review[step.tsField];
        const isNext   = !approved && APPROVAL_CHAIN.slice(0, i).every((s) => !!review[s.tsField]);
        return (
          <React.Fragment key={step.username}>
            <Box
              sx={{
                px: 1.5, py: 0.75,
                borderRadius: 1,
                border: `1px solid ${approved ? '#2E7D32' : isNext ? '#1565C0' : '#bbb'}`,
                backgroundColor: approved ? '#E8F5E9' : isNext ? '#E3F2FD' : '#fafafa',
                textAlign: 'center',
                minWidth: 80,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, color: approved ? '#2E7D32' : isNext ? '#1565C0' : '#999' }}>
                {approved ? '✓ ' : isNext ? '◎ ' : '○ '}{step.label}
              </Typography>
              {approved && review[step.tsField] && (
                <>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                    {new Date(review[step.tsField]!).toLocaleDateString('en-GB')}{' '}
                    {new Date(review[step.tsField]!).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                  {review[step.byField] && (
                    <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', color: '#2E7D32', fontWeight: 600 }}>
                      by {review[step.byField]}
                    </Typography>
                  )}
                </>
              )}
              {isNext && (
                <Typography variant="caption" display="block" sx={{ color: '#1565C0', fontSize: '0.6rem' }}>
                  Pending
                </Typography>
              )}
            </Box>
            {i < APPROVAL_CHAIN.length - 1 && (
              <ArrowIcon sx={{ color: '#bbb', fontSize: 16 }} />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  </Box>
);

// ── Sub-components ─────────────────────────────────────────────────────────────

const StatCard: React.FC<{ label: string; value: number | string; color?: string }> = ({
  label, value, color = '#00695C',
}) => (
  <Card elevation={2}>
    <CardContent sx={{ textAlign: 'center', py: 2 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </CardContent>
  </Card>
);

// ── Main Component ─────────────────────────────────────────────────────────────

const QualityManagement: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const canDeleteReviews = DELETE_ALLOWED_USERS.includes(user?.username || '');
  const canViewAllReviews = user?.role === 'admin' || canDeleteReviews;

  // Main tabs: 0 = S/Off Quality Management, 1 = Bulk Quality Management
  const [mainTab, setMainTab] = useState(0);

  // Sub-tabs (S/Off Quality Management): 0 = New Review, 1 = My Assignments, 2 = Pending Approval, 3 = All Reviews (admin)
  const [tab, setTab] = useState(0);

  // ── Search state ───────────────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [foundCards, setFoundCards] = useState<TrelloCard[]>([]);
  const [selectedCard, setSelectedCard] = useState<TrelloCard | null>(null);
  const [searchedIn, setSearchedIn] = useState<string[]>([]);
  const [manualEntry, setManualEntry] = useState(false);

  // ── Classification form state ──────────────────────────────────────────────
  const [mainRejectionType, setMainRejectionType] = useState<'internal_ocl' | 'external_customer' | ''>('');
  const [subRejectionType, setSubRejectionType] = useState<'printing_department' | 'other_department' | ''>('');
  const [rejectionType, setRejectionType] = useState<'internal' | 'external' | ''>(''); // Legacy support
  const [machineType, setMachineType] = useState<'rotary' | 'digital' | ''>('');
  const [issueType, setIssueType] = useState<'color' | 'layout' | ''>('');
  const [externalIssues, setExternalIssues] = useState<string[]>([]);
  const [externalOtherText, setExternalOtherText] = useState('');
  const [description, setDescription] = useState('');
  const [actionPlan, setActionPlan] = useState('');

  // ── Save state ─────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [lastCreated, setLastCreated] = useState<{ review: QualityReview; assignedTo: string } | null>(null);

  // ── Reviews list state ─────────────────────────────────────────────────────
  const [reviews,          setReviews]          = useState<QualityReview[]>([]);
  const [myAssignments,    setMyAssignments]    = useState<QualityReview[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<QualityReview[]>([]);
  const [stats,            setStats]            = useState<Stats | null>(null);
  const [loadingReviews,   setLoadingReviews]   = useState(false);
  const [viewDialogOpen,   setViewDialogOpen]   = useState(false);
  const [viewReview,       setViewReview]       = useState<QualityReview | null>(null);
  const [reviewSearchQuery, setReviewSearchQuery] = useState('');

  // ── 6M dialog state ────────────────────────────────────────────────────────
  const [sixmOpen,   setSixmOpen]   = useState(false);
  const [sixmReview, setSixmReview] = useState<QualityReview | null>(null);

  // ── Edit dialogs state ─────────────────────────────────────────────────────
  const [editExternalOpen, setEditExternalOpen] = useState(false);
  const [editExternalReview, setEditExternalReview] = useState<QualityReview | null>(null);
  const [editNoIssueOpen, setEditNoIssueOpen] = useState(false);
  const [editNoIssueReview, setEditNoIssueReview] = useState<QualityReview | null>(null);

  // ── Approval state ─────────────────────────────────────────────────────────
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [resubmissionHistory, setResubmissionHistory] = useState<ResubmissionHistoryItem[]>([]);

  // ── Resubmit dialog state ────────────────────────────────────────────────
  const [resubmitOpen, setResubmitOpen] = useState(false);
  const [resubmitReview, setResubmitReview] = useState<QualityReview | null>(null);
  const [resubmitMainRejectionType, setResubmitMainRejectionType] = useState<'internal_ocl' | 'external_customer' | ''>('');
  const [resubmitSubRejectionType, setResubmitSubRejectionType] = useState<'printing_department' | 'other_department' | ''>('');
  const [resubmitMachineType, setResubmitMachineType] = useState<'rotary' | 'digital' | ''>('');
  const [resubmitIssueType, setResubmitIssueType] = useState<'color' | 'layout' | ''>('');
  const [resubmitExternalIssues, setResubmitExternalIssues] = useState<string[]>([]);
  const [resubmitExternalOtherText, setResubmitExternalOtherText] = useState('');
  const [resubmitDescription, setResubmitDescription] = useState('');
  const [resubmitActionPlan, setResubmitActionPlan] = useState('');
  const [resubmitNote, setResubmitNote] = useState('');
  const [resubmitting, setResubmitting] = useState(false);
  const [resubmitError, setResubmitError] = useState<string | null>(null);

  // ── Computed ───────────────────────────────────────────────────────────────
  const preview = mainRejectionType && subRejectionType && machineType
    ? resolveAssignee(
        mainRejectionType as 'internal_ocl' | 'external_customer',
        subRejectionType as 'printing_department' | 'other_department',
        machineType as 'rotary' | 'digital',
        issueType as 'color' | 'layout' | ''
      )
    : null;

  const canSubmit =
    (!!selectedCard || manualEntry) &&
    !!mainRejectionType &&
    !!subRejectionType &&
    !!machineType &&
    (subRejectionType === 'other_department'
      ? externalIssues.length > 0
      : !!issueType);

  const resubmitPreview =
    resubmitMainRejectionType && resubmitSubRejectionType && resubmitMachineType
      ? resolveAssignee(
          resubmitMainRejectionType as 'internal_ocl' | 'external_customer',
          resubmitSubRejectionType as 'printing_department' | 'other_department',
          resubmitMachineType as 'rotary' | 'digital',
          resubmitIssueType as 'color' | 'layout' | ''
        )
      : null;

  const canResubmit =
    !!resubmitReview &&
    !!resubmitMainRejectionType &&
    !!resubmitSubRejectionType &&
    !!resubmitMachineType &&
    (resubmitSubRejectionType === 'other_department'
      ? resubmitExternalIssues.length > 0
      : !!resubmitIssueType);

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    try {
      const data = await qualityManagementService.getStats();
      setStats(data.stats);
    } catch { /* silent */ }
  }, []);

  const fetchReviews = useCallback(async () => {
    setLoadingReviews(true);
    try {
      const data = await qualityManagementService.getAll();
      setReviews(data.reviews || []);
    } catch { /* silent */ } finally {
      setLoadingReviews(false);
    }
  }, []);

  const fetchMyAssignments = useCallback(async () => {
    setLoadingReviews(true);
    try {
      const data = await qualityManagementService.getMyAssignments();
      setMyAssignments(data.reviews || []);
    } catch { /* silent */ } finally {
      setLoadingReviews(false);
    }
  }, []);

  const fetchPendingApprovals = useCallback(async () => {
    setLoadingReviews(true);
    try {
      const data = await qualityManagementService.getPendingApprovals();
      setPendingApprovals(data.reviews || []);
    } catch { /* silent */ } finally {
      setLoadingReviews(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (tab === 1) fetchMyAssignments();
    if (tab === 2) fetchPendingApprovals();
    if (tab === 3) fetchReviews();
  }, [tab, fetchMyAssignments, fetchPendingApprovals, fetchReviews]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSearch = async () => {
    if (!searchInput.trim()) return;
    setSearching(true);
    setSearchError(null);
    setFoundCards([]);
    setSelectedCard(null);
    setSearchedIn([]);
    setManualEntry(false);
    resetForm();

    try {
      const data = await qualityManagementService.searchCard(searchInput.trim());
      setFoundCards(data.cards || []);
      setSearchedIn(data.searched_in || []);
      if (!data.found) {
        setSearchError(
          `No card found matching "${searchInput}". Searched in: ${(data.searched_in || []).join(', ') || 'no target lists found'}`
        );
      }
    } catch (err: any) {
      setSearchError(err.response?.data?.error || 'Failed to search. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const resetForm = () => {
    setMainRejectionType('');
    setSubRejectionType('');
    setRejectionType('');
    setMachineType('');
    setIssueType('');
    setExternalIssues([]);
    setExternalOtherText('');
    setDescription('');
    setActionPlan('');
    setSaveError(null);
    setLastCreated(null);
    setManualEntry(false);
  };

  const handleSelectCard = (card: TrelloCard) => {
    setSelectedCard(card);
    setManualEntry(false);
    resetForm();
  };

  const handleContinueWithoutTrello = () => {
    const syntheticCard: TrelloCard = {
      id:        'manual',
      name:      searchInput.trim(),
      desc:      '',
      labels:    [],
      due:       null,
      url:       '',
      listId:    '',
      listName:  'Not in Trello',
      boardId:   '',
      boardName: 'Manual Entry',
    };
    setSelectedCard(syntheticCard);
    setManualEntry(true);
    setSearchError(null);
    resetForm();
  };

  const handleExternalIssueToggle = (issue: string) => {
    setExternalIssues((prev) =>
      prev.includes(issue) ? prev.filter((i) => i !== issue) : [...prev, issue]
    );
  };

  const handleSubmit = async () => {
    if (!selectedCard || !canSubmit) return;
    setSaving(true);
    setSaveError(null);

    try {
      const resolvedBatchNumber = manualEntry ? searchInput.trim() : selectedCard.name;
      const payload: Parameters<typeof qualityManagementService.create>[0] = {
        trello_card_id:    manualEntry ? undefined : selectedCard.id,
        trello_card_name:  manualEntry ? searchInput.trim() : selectedCard.name,
        batch_number:      resolvedBatchNumber,
        source_list_name:  manualEntry ? undefined : selectedCard.listName,
        source_board_name: manualEntry ? undefined : selectedCard.boardName,
        main_rejection_type:    mainRejectionType as 'internal_ocl' | 'external_customer',
        sub_rejection_type:     subRejectionType as 'printing_department' | 'other_department',
        machine_type:      machineType as 'rotary' | 'digital',
        description:       description.trim() || undefined,
      };

      if (subRejectionType === 'printing_department') {
        payload.issue_type = issueType as 'color' | 'layout';
      } else {
        const issues = externalIssues.filter((i) => i !== 'Other');
        if (externalIssues.includes('Other') && externalOtherText.trim()) {
          issues.push(externalOtherText.trim());
        }
        payload.external_issues = issues;
        payload.external_other_issue = externalIssues.includes('Other') ? externalOtherText.trim() : undefined;
        payload.action_plan  = actionPlan.trim()  || undefined;
      }

      const result = await qualityManagementService.create(payload);
      setLastCreated({ review: result.review, assignedTo: result.assigned_to });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 5000);
      // Reset for next review
      setSelectedCard(null);
      setFoundCards([]);
      setSearchInput('');
      setSearchedIn([]);
      resetForm();
      fetchStats();
    } catch (err: any) {
      setSaveError(err.response?.data?.error || 'Failed to submit review');
    } finally {
      setSaving(false);
    }
  };

  const handleViewReview = async (review: QualityReview) => {
    try {
      // Always load review first; history should never block opening dialog.
      const data = await qualityManagementService.getById(review.id);
      let history: ResubmissionHistoryItem[] = [];
      try {
        const historyData = await qualityManagementService.getResubmissionHistoryByReview(review.id);
        history = historyData.history || [];
      } catch (historyErr) {
        console.error('Failed to load resubmission history:', historyErr);
      }
      setViewReview(data.review);
      setResubmissionHistory(history);
      setViewDialogOpen(true);
    } catch (err: any) {
      console.error('Failed to load review:', err);
      alert('Failed to load review data');
    }
  };

  const openReviewFromHistory = async (reviewId: number) => {
    try {
      const data = await qualityManagementService.getById(reviewId);
      let history: ResubmissionHistoryItem[] = [];
      try {
        const historyData = await qualityManagementService.getResubmissionHistoryByReview(reviewId);
        history = historyData.history || [];
      } catch (historyErr) {
        console.error('Failed to load resubmission history:', historyErr);
      }
      setViewReview(data.review);
      setResubmissionHistory(history);
      setViewDialogOpen(true);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to open review from history');
    }
  };

  const handleStatusChange = async (id: number, status: string) => {
    try {
      await qualityManagementService.updateStatus(id, status);
      fetchMyAssignments();
      fetchReviews();
      fetchStats();
      if (viewReview?.id === id) setViewReview({ ...viewReview, status: status as QualityReview['status'] });
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update status');
    }
  };

  const handleDeleteReview = async (id: number) => {
    if (!canDeleteReviews) return;
    if (!window.confirm('Are you sure you want to delete this review? This action cannot be undone.')) return;

    try {
      await qualityManagementService.deleteReview(id);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      setPendingApprovals((prev) => prev.filter((r) => r.id !== id));
      setMyAssignments((prev) => prev.filter((r) => r.id !== id));
      if (viewReview?.id === id) {
        setViewDialogOpen(false);
        setViewReview(null);
      }
      fetchStats();
      fetchReviews();
      fetchPendingApprovals();
      fetchMyAssignments();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete review');
    }
  };

  const handleDownloadAllReviewsCSV = async () => {
    try {
      const response = await qualityManagementService.exportAllCsv();
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const contentDisposition = response.headers?.['content-disposition'] as string | undefined;
      const match = contentDisposition?.match(/filename="?([^";]+)"?/i);
      a.download = match?.[1] || `quality_all_reviews_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to download reviews CSV');
    }
  };

  const handleOpenSixM = async (review: QualityReview) => {
    try {
      // Fetch the full review data including sixm_entries
      const data = await qualityManagementService.getById(review.id);
      setSixmReview(data.review);
      setSixmOpen(true);
    } catch (err: any) {
      console.error('Failed to load review:', err);
      alert('Failed to load review data');
    }
  };

  const handleSixMSubmitted = () => {
    fetchMyAssignments();
    fetchStats();
  };

  const handleOpenEditExternal = async (review: QualityReview) => {
    try {
      const data = await qualityManagementService.getById(review.id);
      setEditExternalReview(data.review);
      setEditExternalOpen(true);
    } catch (err: any) {
      console.error('Failed to load review:', err);
      alert('Failed to load review data');
    }
  };

  const handleExternalSaved = () => {
    fetchMyAssignments();
    fetchReviews();
    if (viewReview) {
      qualityManagementService.getById(viewReview.id).then(data => setViewReview(data.review));
    }
  };

  const handleOpenEditNoIssue = async (review: QualityReview) => {
    try {
      const data = await qualityManagementService.getById(review.id);
      setEditNoIssueReview(data.review);
      setEditNoIssueOpen(true);
    } catch (err: any) {
      console.error('Failed to load review:', err);
      alert('Failed to load review data');
    }
  };

  const handleNoIssueSaved = () => {
    fetchMyAssignments();
    fetchReviews();
    if (viewReview) {
      qualityManagementService.getById(viewReview.id).then(data => setViewReview(data.review));
    }
  };

  const handleApprove = async (id: number) => {
    setApprovingId(id);
    try {
      await qualityManagementService.approve(id);
      fetchPendingApprovals();
      fetchStats();
      if (viewReview?.id === id) {
        const [updated, historyData] = await Promise.all([
          qualityManagementService.getById(id),
          qualityManagementService.getResubmissionHistoryByReview(id),
        ]);
        setViewReview(updated.review);
        setResubmissionHistory(historyData.history || []);
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to approve');
    } finally {
      setApprovingId(null);
    }
  };

  const handleOpenResubmit = async (review: QualityReview) => {
    try {
      const data = await qualityManagementService.getById(review.id);
      const fullReview: QualityReview = data.review;
      setResubmitReview(fullReview);

      const mainType = fullReview.main_rejection_type
        || (fullReview.rejection_type === 'external' ? 'external_customer' : 'internal_ocl');
      const subType = fullReview.sub_rejection_type
        || (fullReview.rejection_type === 'external' ? 'other_department' : 'printing_department');

      setResubmitMainRejectionType(mainType);
      setResubmitSubRejectionType(subType);
      setResubmitMachineType(fullReview.machine_type || '');
      setResubmitIssueType((fullReview.issue_type as 'color' | 'layout' | null) || '');

      const issues = Array.isArray(fullReview.external_issues)
        ? fullReview.external_issues
        : [];
      setResubmitExternalIssues(issues);
      setResubmitExternalOtherText(fullReview.external_other_issue || '');
      setResubmitDescription(fullReview.description || '');
      setResubmitActionPlan(fullReview.action_plan || '');
      setResubmitNote('');
      setResubmitError(null);
      setResubmitOpen(true);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to load review for resubmission');
    }
  };

  const handleResubmitExternalIssueToggle = (issue: string) => {
    setResubmitExternalIssues((prev) =>
      prev.includes(issue) ? prev.filter((i) => i !== issue) : [...prev, issue]
    );
  };

  const handleSubmitResubmit = async () => {
    if (!resubmitReview || !canResubmit) return;
    setResubmitting(true);
    setResubmitError(null);
    try {
      const payload: Parameters<typeof qualityManagementService.resubmit>[1] = {
        main_rejection_type: resubmitMainRejectionType as 'internal_ocl' | 'external_customer',
        sub_rejection_type: resubmitSubRejectionType as 'printing_department' | 'other_department',
        machine_type: resubmitMachineType as 'rotary' | 'digital',
        description: resubmitDescription.trim() || undefined,
        resubmission_note: resubmitNote.trim() || undefined,
      };

      if (resubmitSubRejectionType === 'printing_department') {
        payload.issue_type = resubmitIssueType as 'color' | 'layout';
      } else {
        const issues = resubmitExternalIssues.filter((i) => i !== 'Other');
        if (resubmitExternalIssues.includes('Other') && resubmitExternalOtherText.trim()) {
          issues.push(resubmitExternalOtherText.trim());
        }
        payload.external_issues = issues;
        payload.external_other_issue = resubmitExternalIssues.includes('Other') ? resubmitExternalOtherText.trim() : undefined;
        payload.action_plan = resubmitActionPlan.trim() || undefined;
      }

      const result = await qualityManagementService.resubmit(resubmitReview.id, payload);
      setResubmitOpen(false);
      setLastCreated({ review: result.review, assignedTo: result.assigned_to });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 5000);
      fetchStats();
      fetchReviews();
      fetchPendingApprovals();
      fetchMyAssignments();
      if (viewReview && resubmitReview && viewReview.id === resubmitReview.id) {
        const historyData = await qualityManagementService.getResubmissionHistoryByReview(viewReview.id);
        setResubmissionHistory(historyData.history || []);
      }
    } catch (err: any) {
      setResubmitError(err.response?.data?.error || 'Failed to resubmit review');
    } finally {
      setResubmitting(false);
    }
  };

  // ── 6M data summary helper ─────────────────────────────────────────────────
  const render6MSummary = (review: QualityReview) => {
    // Use sixm_entries if available, otherwise fall back to old format
    const entries = review.sixm_entries && review.sixm_entries.length > 0 
      ? review.sixm_entries 
      : (review.sixm_category && review.sixm_data ? [{ category: review.sixm_category, ...review.sixm_data, submitted_by: review.sixm_submitted_by }] : []);
    
    if (entries.length === 0) return null;

    console.log('render6MSummary - entries:', entries);

    const categoryIcons: Record<string, React.ReactNode> = {
      man: <Person fontSize="small" />,
      machine: <Build fontSize="small" />,
      material: <Category fontSize="small" />,
      method: <Route fontSize="small" />,
      measurement: <Straighten fontSize="small" />
    };

    return (
      <Stack spacing={2}>
        {entries.map((entry, idx) => {
          const d: any = entry; // Type assertion for dynamic fields
          const rows: Array<[string, string | React.ReactNode]> = [];
          if (d.machine)            rows.push(['Machine', d.machine]);
          if (d.date)               rows.push(['Date', d.date]);
          if (d.shift)              rows.push(['Shift', d.shift]);
          if (d.employee_numbers)   rows.push(['Employee No/s', d.employee_numbers]);
          if (d.employee_names)     rows.push(['Name/s', d.employee_names]);
          if (d.colorist)           rows.push(['Colorist', d.colorist]);
          if (d.route)              rows.push(['Route', d.route]);
          if (d.materials)          rows.push(['Materials', d.materials]);
          if (d.description)        rows.push(['Description', d.description]);
          if (d.action_plan)        rows.push(['Action Plan', d.action_plan]);
          if (d.preventive_action)  rows.push(['Preventive Action', d.preventive_action]);
          if (d.corrective_action)  rows.push(['Corrective Action', d.corrective_action]);
          if (d.links && Array.isArray(d.links) && d.links.length > 0) {
            console.log('Found links for entry:', d.category, 'links:', d.links);
            rows.push(['Evidence Links', (
              <Box component="div" key={`links-${idx}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {d.links.map((url: string, i: number) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1565C0', fontSize: '0.82rem', textDecoration: 'underline' }}>
                    {url}
                  </a>
                ))}
              </Box>
            )]);
          } else {
            console.log('No links found for entry:', d.category, 'd.links:', d.links);
          }
          return (
            <Box key={idx}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                {categoryIcons[d.category] || <SixMIcon fontSize="small" />}
                <Typography variant="subtitle2" color="text.secondary">
                  6M Analysis — <strong style={{ color: '#37474F', textTransform: 'capitalize' }}>{d.category}</strong>
                </Typography>
                {d.submitted_by && (
                  <Chip size="small" label={`by ${d.submitted_by}`} sx={{ fontSize: '0.65rem' }} />
                )}
              </Box>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <tbody>
                  {rows.map(([label, value]) => (
                    <tr key={label}>
                      <td style={{ padding: '4px 12px 4px 0', fontWeight: 600, color: '#555', whiteSpace: 'nowrap', verticalAlign: 'top', width: '36%' }}>{label}</td>
                      <td style={{ padding: '4px 0', color: '#333', verticalAlign: 'top' }}>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
          );
        })}
      </Stack>
    );
  };

  // ── Render helpers ─────────────────────────────────────────────────────────

  const renderReviewTable = (
    data: QualityReview[],
    showAssignee = true,
    showApproveBtn = false,
    showResubmitBtn = false,
    showDeleteBtn = false
  ) => {
    // ── Mobile card view ─────────────────────────────────────────────────────
    if (isMobile) {
      return (
        <Stack spacing={1.5}>
          {data.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">No records found</Typography>
            </Box>
          ) : (
            data.map((r) => (
              <Paper
                key={r.id}
                variant="outlined"
                sx={{ p: 2, cursor: 'pointer', '&:hover': { borderColor: '#00695C' } }}
                onClick={() => handleViewReview(r)}
              >
                {/* Row 1: batch number + status chip */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Box flex={1} mr={1}>
                    <Typography variant="body2" fontWeight={700}>{getDisplayCardNumber(r)}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block"
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                      {r.batch_number}
                    </Typography>
                    {r.description && (
                      <Typography variant="caption" color="text.secondary" display="block"
                        sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>
                        Remark: {r.description}
                      </Typography>
                    )}
                  </Box>
                  <Chip label={statusLabel(r.status)} color={statusColor(r.status) as any} size="small" />
                </Box>
                {/* Row 2: type / machine / assignee chips */}
                <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                  <Chip
                    label={r.rejection_type === 'internal' ? 'Internal' : 'External'}
                    color={r.rejection_type === 'internal' ? 'warning' : 'error'}
                    size="small"
                  />
                  <Chip
                    label={r.machine_type === 'digital' ? 'Digital' : 'Rotary'}
                    color={r.machine_type === 'digital' ? 'info' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                  {showAssignee && (
                    <Chip
                      label={r.assigned_to}
                      size="small"
                      sx={{ backgroundColor: ASSIGNEE_COLORS[r.assigned_to] || '#455A64', color: '#fff', fontWeight: 600 }}
                    />
                  )}
                  {r.sixm_category && (
                    <Chip label={`6M: ${r.sixm_category}`} size="small"
                      sx={{ bgcolor: '#00695C', color: '#fff', fontSize: '0.6rem' }} />
                  )}
                </Box>
                {/* Row 3: submitted by + action buttons */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {r.created_by_name || r.created_by_username} · {new Date(r.created_at).toLocaleDateString('en-GB')}
                  </Typography>
                  <Box display="flex" gap={0.5} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleViewReview(r)}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!['resolved', 'no_issue'].includes(r.status) &&
                      (r.assigned_to === user?.username || user?.role === 'admin') && (
                      <Tooltip title="Edit 6M Analysis">
                        <IconButton size="small" color="primary" onClick={() => handleOpenSixM(r)}>
                          <SixMIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {showApproveBtn && (() => {
                      const stepMap: Record<string, string> = {
                        '6m_submitted':   'AlokaOCL',
                        'aloka_approved': 'AshanOCL',
                        'ashan_approved': 'IsuruOCL',
                        'isuru_approved': 'LasanthaOCL',
                      };
                      const nextStep = stepMap[r.status];
                      const canApprove = nextStep === user?.username || user?.role === 'admin';
                      return canApprove ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={approvingId === r.id ? <CircularProgress size={12} color="inherit" /> : <ApproveIcon />}
                          disabled={approvingId === r.id}
                          onClick={() => handleApprove(r.id)}
                          sx={{ ml: 0.5 }}
                        >
                          Approve
                        </Button>
                      ) : null;
                    })()}
                    {showResubmitBtn && (
                      <Tooltip title="Resubmit Review">
                        <IconButton size="small" color="warning" onClick={() => handleOpenResubmit(r)}>
                          <ResubmitIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {showDeleteBtn && canDeleteReviews && (
                      <Tooltip title="Delete Review">
                        <IconButton size="small" color="error" onClick={() => handleDeleteReview(r.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Paper>
            ))
          )}
        </Stack>
      );
    }

    // ── Desktop table view ────────────────────────────────────────────────────
    return (
      <TableContainer component={Paper} elevation={0} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700 }}>Batch / Card</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Machine</TableCell>
              {showAssignee && <TableCell sx={{ fontWeight: 700 }}>Assigned To</TableCell>}
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Submitted By</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
              {showApproveBtn && <TableCell align="center" sx={{ fontWeight: 700 }}>Approve</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={showAssignee ? 8 : 7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              data.map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  onClick={() => handleViewReview(r)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {getDisplayCardNumber(r)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.batch_number}
                    </Typography>
                    {r.description && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Remark: {r.description}
                      </Typography>
                    )}
                    {r.sixm_category && (
                      <Chip label={`6M: ${r.sixm_category}`} size="small"
                        sx={{ mt: 0.5, fontSize: '0.6rem', bgcolor: '#00695C', color: '#fff' }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={r.rejection_type === 'internal' ? 'Internal' : 'External'}
                      color={r.rejection_type === 'internal' ? 'warning' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={r.machine_type === 'digital' ? 'Digital' : 'Rotary'}
                      color={r.machine_type === 'digital' ? 'info' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  {showAssignee && (
                    <TableCell>
                      <Chip
                        label={r.assigned_to}
                        size="small"
                        sx={{
                          backgroundColor: ASSIGNEE_COLORS[r.assigned_to] || '#455A64',
                          color: '#fff',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Chip label={statusLabel(r.status)} color={statusColor(r.status) as any} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{r.created_by_name || r.created_by_username}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(r.created_at).toLocaleDateString('en-GB')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewReview(r)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* 6M edit button — for all reviews (assigned user or admin) */}
                      {!['resolved', 'no_issue'].includes(r.status) &&
                        (r.assigned_to === user?.username || user?.role === 'admin') && (
                        <Tooltip title="Edit 6M Analysis">
                          <IconButton size="small" color="primary" onClick={() => handleOpenSixM(r)}>
                            <SixMIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {showResubmitBtn && (
                        <Tooltip title="Resubmit Review">
                          <IconButton size="small" color="warning" onClick={() => handleOpenResubmit(r)}>
                            <ResubmitIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {showDeleteBtn && canDeleteReviews && (
                        <Tooltip title="Delete Review">
                          <IconButton size="small" color="error" onClick={() => handleDeleteReview(r.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  {showApproveBtn && (() => {
                    const stepMap: Record<string, string> = {
                      '6m_submitted':   'AlokaOCL',
                      'aloka_approved': 'AshanOCL',
                      'ashan_approved': 'IsuruOCL',
                      'isuru_approved': 'LasanthaOCL',
                    };
                    const nextStep = stepMap[r.status];
                    const canApprove = nextStep === user?.username || user?.role === 'admin';
                    return canApprove ? (
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={approvingId === r.id ? <CircularProgress size={12} color="inherit" /> : <ApproveIcon />}
                          disabled={approvingId === r.id}
                          onClick={() => handleApprove(r.id)}
                        >
                          Approve
                        </Button>
                      </TableCell>
                    ) : null;
                  })()}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <QualityIcon sx={{ fontSize: 36, color: '#00695C' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#00695C', lineHeight: 1.2, fontSize: { xs: '1.2rem', sm: '1.6rem', md: '2.125rem' } }}>
              Quality Management System
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Rejection review & approval workflow
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={() => { fetchStats(); if (tab === 1) fetchMyAssignments(); if (tab === 2) fetchReviews(); }}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Main Tabs */}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f5f5f5' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="S/Off Quality Management" icon={<QualityIcon />} iconPosition="start" />
          <Tab label="Bulk Quality Management" icon={<BulkIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* S/Off Quality Management Tab Content */}
      {mainTab === 0 && (
        <>
          {/* Success banner */}
          {saveSuccess && lastCreated && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveSuccess(false)}>
              Quality review submitted successfully. Assigned to{' '}
              <strong>{lastCreated.assignedTo}</strong>.
            </Alert>
          )}

          {/* Stats cards */}
          {stats && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { label: 'Total Reviews',  value: stats.total,          color: '#00695C' },
                { label: 'Pending',        value: stats.pending,         color: '#757575' },
                { label: 'In Review',      value: stats.in_review,       color: '#F57F17' },
                { label: '6M Submitted',   value: stats.sixm_submitted,  color: '#1565C0' },
                { label: 'In Approval',    value: stats.in_approval,     color: '#7B1FA2' },
                { label: 'Resolved',       value: stats.resolved,        color: '#2E7D32' },
                { label: 'Internal',       value: stats.internal,        color: '#E65100' },
                { label: 'External',       value: stats.external,        color: '#B71C1C' },
              ].map((s) => (
                <Grid item xs={6} sm={4} md={3} lg={1.5} key={s.label}>
                  <StatCard label={s.label} value={s.value} color={s.color} />
                </Grid>
              ))}
            </Grid>
          )}

      {/* Sub-Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="New Review"       icon={<AssignIcon />}    iconPosition="start" />
          <Tab label="My Assignments"   icon={<CheckCircleIcon />} iconPosition="start" />
          <Tab label="Pending Approval" icon={<ApproveIcon />}   iconPosition="start" />
          {canViewAllReviews && (
            <Tab label="All Reviews" icon={<QualityIcon />} iconPosition="start" />
          )}
        </Tabs>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {/* ── Tab 0: New Review ─────────────────────────────────────────── */}
          {tab === 0 && (
            <Box>
              {/* Step 1 – Search */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#00695C' }}>
                Step 1 — Search Card
              </Typography>
              <Box display="flex" gap={2} alignItems="flex-start" mb={1} flexDirection={{ xs: 'column', sm: 'row' }}>
                <TextField
                  label="Batch / Card Number"
                  size="small"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  fullWidth
                  placeholder="e.g. OCL-2025-0123"
                />
                <Button
                  variant="contained"
                  sx={{ bgcolor: '#00695C', whiteSpace: 'nowrap', flexShrink: 0 }}
                  onClick={handleSearch}
                  disabled={searching || !searchInput.trim()}
                  startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                  fullWidth={isMobile}
                >
                  {searching ? 'Searching...' : 'Search'}
                </Button>
              </Box>

              {searchedIn.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Searched in: {searchedIn.join(' · ')}
                </Typography>
              )}

              {searchError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>{searchError}</Typography>
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      onClick={handleContinueWithoutTrello}
                    >
                      Continue Without Trello Card
                    </Button>
                  </Box>
                </Alert>
              )}

              {/* Card results */}
              {foundCards.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {foundCards.length} card{foundCards.length > 1 ? 's' : ''} found — select one to proceed:
                  </Typography>
                  <Stack spacing={1}>
                    {foundCards.map((card) => (
                      <Paper
                        key={card.id}
                        variant="outlined"
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          borderColor: selectedCard?.id === card.id ? '#00695C' : 'divider',
                          borderWidth: selectedCard?.id === card.id ? 2 : 1,
                          backgroundColor: selectedCard?.id === card.id ? '#E8F5E9' : 'white',
                          '&:hover': { borderColor: '#00695C', backgroundColor: '#F1F8E9' },
                        }}
                        onClick={() => handleSelectCard(card)}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box flex={1}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {card.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Board: <strong>{card.boardName}</strong> · List: <strong>{card.listName}</strong>
                            </Typography>
                            {card.labels?.length > 0 && (
                              <Box mt={0.5} display="flex" gap={0.5} flexWrap="wrap">
                                {card.labels.map((l) => (
                                  <Chip key={l.id} label={l.name || l.color} size="small"
                                    sx={{ backgroundColor: l.color, color: '#fff', height: 18, fontSize: '0.65rem' }} />
                                ))}
                              </Box>
                            )}
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            {selectedCard?.id === card.id && (
                              <CheckCircleIcon sx={{ color: '#00695C' }} />
                            )}
                            <Tooltip title="Open in Trello">
                              <IconButton
                                size="small"
                                href={card.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Step 2 – Classification */}
              {selectedCard && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#00695C' }}>
                    Step 2 — Classify Rejection
                  </Typography>

                  {manualEntry && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <strong>Manual entry mode</strong> — no Trello card found for <strong>{searchInput.trim()}</strong>. The rejection will be logged with the batch number only.
                    </Alert>
                  )}

                  <Grid container spacing={3}>
                    {/* Main Rejection Classification */}
                    <Grid item xs={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ fontWeight: 700, color: '#333' }}>
                          Main Classification *
                        </FormLabel>
                        <RadioGroup
                          row
                          value={mainRejectionType}
                          onChange={(e) => {
                            setMainRejectionType(e.target.value as any);
                            setSubRejectionType('');
                            setIssueType('');
                            setExternalIssues([]);
                            setExternalOtherText('');
                          }}
                        >
                          <FormControlLabel value="internal_ocl" control={<Radio />} label="Internal Rejection - OCL" />
                          <FormControlLabel value="external_customer" control={<Radio />} label="External Rejection - Customer" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    {/* Sub-classification (Department) */}
                    {mainRejectionType && (
                      <Grid item xs={12}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend" sx={{ fontWeight: 700, color: '#333' }}>
                            Department Classification *
                          </FormLabel>
                          <RadioGroup
                            row
                            value={subRejectionType}
                            onChange={(e) => {
                              setSubRejectionType(e.target.value as any);
                              setIssueType('');
                              setExternalIssues([]);
                              setExternalOtherText('');
                            }}
                          >
                            <FormControlLabel 
                              value="printing_department" 
                              control={<Radio />} 
                              label={`${mainRejectionType === 'internal_ocl' ? 'Internal' : 'External'} Rejection - Printing Department`} 
                            />
                            <FormControlLabel 
                              value="other_department" 
                              control={<Radio />} 
                              label={`${mainRejectionType === 'internal_ocl' ? 'Internal' : 'External'} Rejection - Other Department`} 
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    )}

                    {/* Machine Type */}
                    {subRejectionType && (
                      <Grid item xs={12} sm={6}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend" sx={{ fontWeight: 700, color: '#333' }}>
                            Machine Type *
                          </FormLabel>
                          <RadioGroup
                            row
                            value={machineType}
                            onChange={(e) => setMachineType(e.target.value as any)}
                          >
                            <FormControlLabel value="rotary" control={<Radio />} label="Rotary" />
                            <FormControlLabel value="digital" control={<Radio />} label="Digital" />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    )}

                    {/* Printing Department: Issue Type */}
                    {subRejectionType === 'printing_department' && machineType && (
                      <Grid item xs={12} sm={6}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend" sx={{ fontWeight: 700, color: '#333' }}>
                            Issue Type *
                          </FormLabel>
                          <RadioGroup
                            row
                            value={issueType}
                            onChange={(e) => setIssueType(e.target.value as any)}
                          >
                            <FormControlLabel value="color" control={<Radio />} label="Color Issue" />
                            <FormControlLabel value="layout" control={<Radio />} label="Layout Issue" />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    )}

                    {subRejectionType && machineType && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Remark"
                          multiline
                          rows={3}
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Add context for the assignee and approvers..."
                        />
                      </Grid>
                    )}

                    {/* Other Department: Issue Checklist */}
                    {subRejectionType === 'other_department' && machineType && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <FormControl component="fieldset">
                            <FormLabel component="legend" sx={{ fontWeight: 700, color: '#333' }}>
                              Issues Identified * (tick all that apply)
                            </FormLabel>
                            <FormGroup>
                              {EXTERNAL_ISSUE_OPTIONS.map((option) => (
                                <FormControlLabel
                                  key={option}
                                  control={
                                    <Checkbox
                                      checked={externalIssues.includes(option)}
                                      onChange={() => handleExternalIssueToggle(option)}
                                    />
                                  }
                                  label={option}
                                />
                              ))}
                            </FormGroup>
                            {externalIssues.includes('Other') && (
                              <TextField
                                size="small"
                                label="Specify other issue"
                                value={externalOtherText}
                                onChange={(e) => setExternalOtherText(e.target.value)}
                                sx={{ mt: 1 }}
                                fullWidth
                              />
                            )}
                          </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Action Plan"
                            multiline
                            rows={3}
                            value={actionPlan}
                            onChange={(e) => setActionPlan(e.target.value)}
                            placeholder="Describe the planned corrective action..."
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>

                  {/* Assignment Preview */}
                  {preview && (
                    <Paper
                      sx={{
                        mt: 3, p: 2,
                        border: `2px solid ${ASSIGNEE_COLORS[preview] || '#455A64'}`,
                        borderRadius: 2,
                        backgroundColor: '#fafafa',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: ASSIGNEE_COLORS[preview] || '#455A64', width: 48, height: 48 }}>
                          {preview[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            This review will be assigned to:
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: ASSIGNEE_COLORS[preview] || '#455A64' }}>
                            {preview}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {mainRejectionType === 'internal_ocl' ? 'Internal Rejection - OCL' : 'External Rejection - Customer'} →{' '}
                            {subRejectionType === 'printing_department' 
                              ? `Printing Dept · ${machineType} · ${issueType} issue`
                              : 'Other Department'}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  )}

                  {saveError && (
                    <Alert severity="error" sx={{ mt: 2 }} onClose={() => setSaveError(null)}>
                      {saveError}
                    </Alert>
                  )}

                  <Box mt={3}>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth={isMobile}
                      sx={{ bgcolor: '#00695C' }}
                      onClick={handleSubmit}
                      disabled={!canSubmit || saving}
                      startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <AssignIcon />}
                    >
                      {saving ? 'Submitting...' : 'Submit & Assign'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* ── Tab 1: My Assignments ──────────────────────────────────────── */}
          {tab === 1 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box flex={1}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#00695C' }}>My Assignments</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Reviews assigned to you. Use the <SixMIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> button to submit 6M analysis.
                  </Typography>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    size="small"
                    placeholder="Search batch/card..."
                    value={reviewSearchQuery}
                    onChange={(e) => setReviewSearchQuery(e.target.value)}
                    sx={{ width: 220 }}
                    InputProps={{
                      startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: '#999' }} />
                    }}
                  />
                  <Button size="small" startIcon={<RefreshIcon />} onClick={fetchMyAssignments}>Refresh</Button>
                </Box>
              </Box>
              {loadingReviews ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
              ) : (
                renderReviewTable(
                  myAssignments.filter(r => 
                    !reviewSearchQuery.trim() || 
                    r.batch_number.toLowerCase().includes(reviewSearchQuery.toLowerCase()) ||
                    r.trello_card_name.toLowerCase().includes(reviewSearchQuery.toLowerCase())
                  ), 
                  false
                )
              )}
            </Box>
          )}

          {/* ── Tab 2: Pending Approval ────────────────────────────────────── */}
          {tab === 2 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box flex={1}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1565C0' }}>Pending Approval</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Reviews awaiting your approval in the chain: AlokaOCL → AshanOCL → IsuruOCL → LasanthaOCL
                  </Typography>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    size="small"
                    placeholder="Search batch/card..."
                    value={reviewSearchQuery}
                    onChange={(e) => setReviewSearchQuery(e.target.value)}
                    sx={{ width: 220 }}
                    InputProps={{
                      startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: '#999' }} />
                    }}
                  />
                  <Button size="small" startIcon={<RefreshIcon />} onClick={fetchPendingApprovals}>Refresh</Button>
                </Box>
              </Box>
              {loadingReviews ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
              ) : pendingApprovals.filter(r => 
                  !reviewSearchQuery.trim() || 
                  r.batch_number.toLowerCase().includes(reviewSearchQuery.toLowerCase()) ||
                  r.trello_card_name.toLowerCase().includes(reviewSearchQuery.toLowerCase())
                ).length === 0 ? (
                <Alert severity="info">{reviewSearchQuery.trim() ? 'No matching reviews found.' : 'No reviews currently pending your approval.'}</Alert>
              ) : (
                renderReviewTable(
                  pendingApprovals.filter(r => 
                    !reviewSearchQuery.trim() || 
                    r.batch_number.toLowerCase().includes(reviewSearchQuery.toLowerCase()) ||
                    r.trello_card_name.toLowerCase().includes(reviewSearchQuery.toLowerCase())
                  ), 
                  true, 
                  true,
                  true,
                  false
                )
              )}
            </Box>
          )}

          {/* ── Tab 3: All Reviews (admin only) ───────────────────────────── */}
          {tab === 3 && canViewAllReviews && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Box flex={1}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#00695C' }}>All Reviews</Typography>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadAllReviewsCSV}
                    disabled={reviews.length === 0}
                  >
                    Download CSV
                  </Button>
                  <TextField
                    size="small"
                    placeholder="Search batch/card..."
                    value={reviewSearchQuery}
                    onChange={(e) => setReviewSearchQuery(e.target.value)}
                    sx={{ width: 220 }}
                    InputProps={{
                      startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: '#999' }} />
                    }}
                  />
                  <Button size="small" startIcon={<RefreshIcon />} onClick={fetchReviews}>Refresh</Button>
                </Box>
              </Box>
              {loadingReviews ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
              ) : (
                renderReviewTable(
                  reviews.filter(r => 
                    !reviewSearchQuery.trim() || 
                    r.batch_number.toLowerCase().includes(reviewSearchQuery.toLowerCase()) ||
                    r.trello_card_name.toLowerCase().includes(reviewSearchQuery.toLowerCase())
                  ),
                  true,
                  false,
                  true,
                  true
                )
              )}
            </Box>
          )}
        </Box>
      </Paper>
        </>
      )}

      {/* Bulk Quality Management Tab Content */}
      {mainTab === 1 && <BulkQualityManagement />}

      {/* ── View Review Dialog ──────────────────────────────────────────────── */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth scroll="paper" fullScreen={isMobile}>
        <DialogTitle sx={{ backgroundColor: '#00695C', color: 'white' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <QualityIcon />
            Quality Review — {viewReview ? getDisplayCardNumber(viewReview) : ''}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {viewReview && (
            <Stack spacing={2}>
              {/* Card info */}
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Trello Card
                </Typography>
                <Typography variant="body1" fontWeight={600}>{viewReview.trello_card_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Batch: {viewReview.batch_number} · {viewReview.source_board_name} / {viewReview.source_list_name}
                </Typography>
              </Paper>

              {/* Classification */}
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip
                  label={viewReview.rejection_type === 'internal' ? 'Internal Rejection' : 'External Rejection'}
                  color={viewReview.rejection_type === 'internal' ? 'warning' : 'error'}
                />
                <Chip
                  label={viewReview.machine_type === 'digital' ? 'Digital' : 'Rotary'}
                  color={viewReview.machine_type === 'digital' ? 'info' : 'default'}
                  variant="outlined"
                />
                {viewReview.issue_type && (
                  <Chip label={`${viewReview.issue_type.charAt(0).toUpperCase() + viewReview.issue_type.slice(1)} Issue`} />
                )}
                <Chip label={statusLabel(viewReview.status)} color={statusColor(viewReview.status) as any} />
              </Box>

              {/* External rejection fields */}
              {viewReview.rejection_type === 'external' && (
                <>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                      External Rejection Details
                    </Typography>
                    {viewReview.status !== 'resolved' &&
                      (viewReview.assigned_to === user?.username || user?.role === 'admin') && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => { setViewDialogOpen(false); handleOpenEditExternal(viewReview); }}
                        sx={{ textTransform: 'none' }}
                      >
                        Edit Details
                      </Button>
                    )}
                  </Box>
                  {viewReview.external_issues && viewReview.external_issues.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Issues Identified</Typography>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {(Array.isArray(viewReview.external_issues)
                          ? viewReview.external_issues
                          : JSON.parse(viewReview.external_issues as any)
                        ).map((issue: string) => (
                          <Chip key={issue} label={issue} size="small" color="error" variant="outlined" />
                        ))}
                      </Box>
                    </Box>
                  )}
                  {viewReview.description && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Description</Typography>
                      <Typography variant="body2">{viewReview.description}</Typography>
                    </Box>
                  )}
                  {viewReview.action_plan && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Action Plan</Typography>
                      <Typography variant="body2">{viewReview.action_plan}</Typography>
                    </Box>
                  )}
                </>
              )}

              {/* No Issue section */}
              {viewReview.status === 'no_issue' && (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                      No Issue
                    </Typography>
                    {(viewReview.assigned_to === user?.username || user?.role === 'admin') && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        onClick={() => { setViewDialogOpen(false); handleOpenEditNoIssue(viewReview); }}
                        sx={{ textTransform: 'none' }}
                      >
                        Edit Remarks
                      </Button>
                    )}
                  </Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    This review was marked as <strong>No Issue</strong> — no underlying problem requiring corrective action.
                  </Alert>
                  {viewReview.no_issue_remarks && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Remarks</Typography>
                      <Typography variant="body2">{viewReview.no_issue_remarks}</Typography>
                    </Box>
                  )}
                </Box>
              )}

              <Divider />

              {/* Resubmission History */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Resubmission History
                </Typography>
                {resubmissionHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No resubmissions recorded for this review chain.</Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {resubmissionHistory.map((h) => {
                      const isCurrentSource = viewReview.id === h.original_review_id;
                      const isCurrentTarget = viewReview.id === h.new_review_id;
                      return (
                        <Paper key={h.id} variant="outlined" sx={{ p: 1.5 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Review #{h.original_review_id} → Review #{h.new_review_id}
                            </Typography>
                            <Chip
                              size="small"
                              color={isCurrentSource ? 'warning' : isCurrentTarget ? 'success' : 'default'}
                              label={isCurrentSource ? 'From Current Review' : isCurrentTarget ? 'Current Review' : 'Related'}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {new Date(h.created_at).toLocaleString('en-GB')} · by {h.resubmitted_by_username || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            Status before resubmit: {h.original_status ? statusLabel(h.original_status) : 'N/A'}
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openReviewFromHistory(h.original_review_id)}
                              disabled={viewReview.id === h.original_review_id}
                            >
                              Open Original #{h.original_review_id}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => openReviewFromHistory(h.new_review_id)}
                              disabled={viewReview.id === h.new_review_id}
                            >
                              Open New #{h.new_review_id}
                            </Button>
                          </Box>
                          {h.resubmission_note && (
                            <Typography variant="body2" sx={{ mt: 0.75 }}>
                              Note: {h.resubmission_note}
                            </Typography>
                          )}
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Box>

              {/* Assignment & status */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Assignment</Typography>
                <Chip
                  label={viewReview.assigned_to}
                  sx={{ bgcolor: ASSIGNEE_COLORS[viewReview.assigned_to] || '#455A64', color: '#fff', fontWeight: 700 }}
                />
              </Box>

              {viewReview.description && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Remark</Typography>
                  <Typography variant="body2">{viewReview.description}</Typography>
                </Box>
              )}

              {/* 6M Analysis section */}
              <Box>
                <Divider sx={{ my: 1 }} />
                {viewReview.sixm_category ? (
                  render6MSummary(viewReview)
                ) : (
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="body2" color="text.secondary">No 6M analysis submitted yet.</Typography>
                    {!['resolved', 'no_issue'].includes(viewReview.status) &&
                     (viewReview.assigned_to === user?.username || user?.role === 'admin') && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<SixMIcon />}
                        onClick={() => { setViewDialogOpen(false); handleOpenSixM(viewReview); }}
                      >
                        Submit 6M Analysis
                      </Button>
                    )}
                  </Box>
                )}
              </Box>

              {/* Approval chain */}
              <Box>
                  <Divider sx={{ my: 1 }} />
                  <ApprovalChainDisplay review={viewReview} />
                  {/* Approve button if current user is next in chain */}
                  {viewReview.status !== 'resolved' && viewReview.status !== 'no_issue' && (() => {
                    const stepMap: Record<string, string> = {
                      '6m_submitted':   'AlokaOCL',
                      'aloka_approved': 'AshanOCL',
                      'ashan_approved': 'IsuruOCL',
                      'isuru_approved': 'LasanthaOCL',
                    };
                    const nextStep = stepMap[viewReview.status];
                    // Only the next approver in the chain (or admin) can approve
                    const canApprove = nextStep === user?.username || user?.role === 'admin';
                    return canApprove ? (
                      <Button
                        sx={{ mt: 1.5 }}
                        variant="contained"
                        color="success"
                        startIcon={approvingId === viewReview.id ? <CircularProgress size={14} color="inherit" /> : <ApproveIcon />}
                        disabled={approvingId === viewReview.id}
                        onClick={() => handleApprove(viewReview.id)}
                      >
                        Approve
                      </Button>
                    ) : null;
                  })()}
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Update Status
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {STATUS_ORDER.map((s) => {
                    const sc = statusColor(s);
                    const btnColor = (sc === 'default' ? 'primary' : sc) as any;
                    return (
                      <Button
                        key={s}
                        size="small"
                        variant={viewReview.status === s ? 'contained' : 'outlined'}
                        color={btnColor}
                        onClick={() => handleStatusChange(viewReview.id, s)}
                      >
                        {statusLabel(s)}
                      </Button>
                    );
                  })}
                </Box>
              </Box>

              <Typography variant="caption" color="text.secondary">
                Submitted by {viewReview.created_by_name} · {new Date(viewReview.created_at).toLocaleString('en-GB')}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* ── 6M Analysis Dialog ───────────────────────────────────────────── */}
      <SixMAnalysisDialog
        open={sixmOpen}
        review={sixmReview as any}
        onClose={() => setSixmOpen(false)}
        onSubmitted={handleSixMSubmitted}
      />

      {/* ── Edit External Rejection Dialog ───────────────────────────────── */}
      <EditExternalRejectionDialog
        open={editExternalOpen}
        review={editExternalReview}
        onClose={() => setEditExternalOpen(false)}
        onSaved={handleExternalSaved}
      />

      {/* ── Edit No Issue Dialog ──────────────────────────────────────────── */}
      <EditNoIssueDialog
        open={editNoIssueOpen}
        review={editNoIssueReview}
        onClose={() => setEditNoIssueOpen(false)}
        onSaved={handleNoIssueSaved}
      />

      {/* ── Resubmit Dialog ─────────────────────────────────────────────── */}
      <Dialog open={resubmitOpen} onClose={() => setResubmitOpen(false)} maxWidth="md" fullWidth scroll="paper" fullScreen={isMobile}>
        <DialogTitle sx={{ backgroundColor: '#E65100', color: 'white' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <ResubmitIcon />
            Resubmit Review — {resubmitReview ? getDisplayCardNumber(resubmitReview) : ''}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {resubmitReview && (
            <Stack spacing={2}>
              <Alert severity="info">
                This creates a new review with updated classification and assignment, while preserving the previous review as history.
              </Alert>

              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 700, color: '#333' }}>Main Classification *</FormLabel>
                <RadioGroup
                  row
                  value={resubmitMainRejectionType}
                  onChange={(e) => {
                    setResubmitMainRejectionType(e.target.value as any);
                    setResubmitSubRejectionType('');
                    setResubmitIssueType('');
                    setResubmitExternalIssues([]);
                    setResubmitExternalOtherText('');
                  }}
                >
                  <FormControlLabel value="internal_ocl" control={<Radio />} label="Internal Rejection - OCL" />
                  <FormControlLabel value="external_customer" control={<Radio />} label="External Rejection - Customer" />
                </RadioGroup>
              </FormControl>

              {resubmitMainRejectionType && (
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 700, color: '#333' }}>Department Classification *</FormLabel>
                  <RadioGroup
                    row
                    value={resubmitSubRejectionType}
                    onChange={(e) => {
                      setResubmitSubRejectionType(e.target.value as any);
                      setResubmitIssueType('');
                      setResubmitExternalIssues([]);
                      setResubmitExternalOtherText('');
                    }}
                  >
                    <FormControlLabel value="printing_department" control={<Radio />} label="Printing Department" />
                    <FormControlLabel value="other_department" control={<Radio />} label="Other Department" />
                  </RadioGroup>
                </FormControl>
              )}

              {resubmitSubRejectionType && (
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 700, color: '#333' }}>Machine Type *</FormLabel>
                  <RadioGroup row value={resubmitMachineType} onChange={(e) => setResubmitMachineType(e.target.value as any)}>
                    <FormControlLabel value="rotary" control={<Radio />} label="Rotary" />
                    <FormControlLabel value="digital" control={<Radio />} label="Digital" />
                  </RadioGroup>
                </FormControl>
              )}

              {resubmitSubRejectionType === 'printing_department' && resubmitMachineType && (
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 700, color: '#333' }}>Issue Type *</FormLabel>
                  <RadioGroup row value={resubmitIssueType} onChange={(e) => setResubmitIssueType(e.target.value as any)}>
                    <FormControlLabel value="color" control={<Radio />} label="Color Issue" />
                    <FormControlLabel value="layout" control={<Radio />} label="Layout Issue" />
                  </RadioGroup>
                </FormControl>
              )}

              {resubmitSubRejectionType && resubmitMachineType && (
                <TextField
                  fullWidth
                  label="Remark"
                  multiline
                  rows={3}
                  value={resubmitDescription}
                  onChange={(e) => setResubmitDescription(e.target.value)}
                />
              )}

              {resubmitSubRejectionType === 'other_department' && resubmitMachineType && (
                <>
                  <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ fontWeight: 700, color: '#333' }}>
                      Issues Identified * (tick all that apply)
                    </FormLabel>
                    <FormGroup>
                      {EXTERNAL_ISSUE_OPTIONS.map((option) => (
                        <FormControlLabel
                          key={option}
                          control={
                            <Checkbox
                              checked={resubmitExternalIssues.includes(option)}
                              onChange={() => handleResubmitExternalIssueToggle(option)}
                            />
                          }
                          label={option}
                        />
                      ))}
                    </FormGroup>
                    {resubmitExternalIssues.includes('Other') && (
                      <TextField
                        size="small"
                        label="Specify other issue"
                        value={resubmitExternalOtherText}
                        onChange={(e) => setResubmitExternalOtherText(e.target.value)}
                        sx={{ mt: 1 }}
                        fullWidth
                      />
                    )}
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Action Plan"
                    multiline
                    rows={3}
                    value={resubmitActionPlan}
                    onChange={(e) => setResubmitActionPlan(e.target.value)}
                  />
                </>
              )}

              {resubmitPreview && (
                <Paper sx={{ p: 2, border: `2px solid ${ASSIGNEE_COLORS[resubmitPreview] || '#455A64'}`, borderRadius: 2, backgroundColor: '#fafafa' }}>
                  <Typography variant="body2" color="text.secondary">This resubmission will be assigned to:</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: ASSIGNEE_COLORS[resubmitPreview] || '#455A64' }}>
                    {resubmitPreview}
                  </Typography>
                </Paper>
              )}

              <TextField
                fullWidth
                label="Resubmission Note (optional)"
                multiline
                rows={2}
                value={resubmitNote}
                onChange={(e) => setResubmitNote(e.target.value)}
              />

              {resubmitError && (
                <Alert severity="error" onClose={() => setResubmitError(null)}>{resubmitError}</Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResubmitOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={resubmitting ? <CircularProgress size={14} color="inherit" /> : <ResubmitIcon />}
            onClick={handleSubmitResubmit}
            disabled={!canResubmit || resubmitting}
          >
            {resubmitting ? 'Resubmitting...' : 'Resubmit & Assign'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QualityManagement;
